<template>
    <div class="fullwidth-wrapper fullwidth-wrapper--momentum">
        <div
            :id="id"
            class="momentum-chart balloon-chart fullwidth-wrapper__content"
            @mousedown="handleMouseDown"
            @mouseup="handleMouseUp"
            @mousemove="handleMouseMove"
            @mouseleave="handleMouseLeave">
        </div>
    </div>
</template>

<script>
// @ is an alias to /src

import Vue from "vue";

import d3 from "@/utils/d3/d3.js";

import dayjs from "dayjs";
import hexToRgba from "hex-to-rgba";

import {ELEMENTS} from "@/utils/constants/elements.js";

export default {
    name: "BalloonChart",
    props: {
        type: {
            type: String,
            required: false,
            default: "year"
        },
        data: {
            type: Array,
            required: true
        },
        userBirthdate: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            x: 0,
            y: 0,
            top: 0,
            left: 0,
            isMouseDown: false,
            isDragEnable: false,
            id: "balloon-chart",
            currentYear: String(new Date().getFullYear()),
            today: new Date(),
            userAniversary: dayjs(this.userBirthdate).format("MMDD")
        };
    },
    computed: {
        screenWidth() {
            return this.$store.getters["responsiveModule/getCurrentWindowWidth"];
        },
        todayMonthDay() {
            const dd = String(this.today.getDate()).padStart(2, "0");
            const mm = String(this.today.getMonth() + 1).padStart(2, "0"); //January is 0!

            return mm + dd;
        },
        cleanedData() {
            const cleanedData = this.data.map((item) => {
                let x;

                if (this.type === "year") {
                    x = d3.timeParse("%Y%m%d")(item.datecalendar);
                } else if (this.type === "month") {
                    x = d3.timeParse("%Y%m%d")(item.datecalendar);
                }

                let isCurrentYear = false;

                const itemYear = dayjs(x).year();

                if (parseInt(this.currentYear) - 1 === itemYear) {
                    if (dayjs(this.todayMonthDay).isBefore(this.userAniversary)) {
                        isCurrentYear = true;
                    }
                } else if (parseInt(this.currentYear) === itemYear) {
                    if (dayjs(this.todayMonthDay).isAfter(this.userAniversary)) {
                        isCurrentYear = true;
                    }
                }

                return {
                    "x": x,
                    "y": item.y,
                    "value": item.score,
                    "element": item.element,
                    "domaine": item.cycle_domaine,
                    "iday": item.iday,
                    "color": "#" + item.couleur,
                    "desc": item.desc,
                };
            });

            return cleanedData;
        },
    },
    watch: {
        type (newVal, oldVal) {
            if (newVal !== oldVal) {
                this.resetLifecycleChart();
                this.buildLifecycleChart();
            }
        },
        screenWidth(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.resetLifecycleChart();
                this.buildLifecycleChart();
            }
        }
    },
    mounted: function() {
        this.buildLifecycleChart();
    },
    methods: {
        resetLifecycleChart() {
            const wrapper = document.getElementById(this.id);
            wrapper.innerHTML = "";
        },
        buildLifecycleChart() {
            Vue.nextTick(() => {

                const radiusRange = this.cleanedData.map((d) => {
                    return d.value * 2;
                });

                const maxRadiusValueRange = Math.max(...radiusRange);

                const yValueRange = d3.extent(this.cleanedData, function(d) {
                    return d.y;
                });

                const adaptedYValueRange = [0, yValueRange[1] + 10];

                // set the dimensions and margins of the graph
                const margin = {top: 50, right: 30, bottom: 100, left: 40};
                const width = ((maxRadiusValueRange * 1.5) * (this.cleanedData.length));
                const height = 378 + margin.top + margin.bottom;

                // append the svg object to the body of the page
                var svg = d3.select("#" + this.id)
                    .append("svg")
                    .attr("preserveAspectRatio", "xMinYMin meet")
                    .attr("width", width + 75 + 20 + margin.right)
                    .attr("height", height + margin.top)
                    .attr("viewBox", `0 0 ${width + 75 + 10} ${height + margin.top + 20}`)
                    .append("g")
                    .attr("transform", "translate(" + margin.left + ", 0)");

                let adaptedCleanedData = this.cleanedData.map(item => item.x);
                const adaptedCleanedDataFirst = dayjs(adaptedCleanedData[0]).startOf("year").toDate();
                const adaptedCleanedDataLast = dayjs(adaptedCleanedData[adaptedCleanedData.length - 1]).add(1, "year").startOf("year").toDate();

                if (this.type === "year") {
                    adaptedCleanedData.unshift(adaptedCleanedDataFirst);
                    adaptedCleanedData.push(adaptedCleanedDataLast);
                }

                let x = d3.scaleTime()
                    .domain(d3.extent(adaptedCleanedData))
                    .range([0, width]);

                let xAxisRangeType = d3.timeYear;

                if (this.type === "month") {
                    xAxisRangeType = d3.timeMonth;
                }

                const xAxis = g => g
                    .attr("transform", `translate(0, ${height + 0})`)
                    .attr("class", "x-axis")
                    .call(d3.axisBottom(x)
                        .ticks(xAxisRangeType)
                        .tickSizeInner(15)
                    )
                    .call(g => g.select(".domain").remove());

                const xAxisSep = g => g
                    .attr("transform", `translate(0, ${height})`)
                    .attr("class", "x-axis-sep")
                    .call(d3.axisBottom(x)
                        .ticks(d3.timeMonth)
                        .tickFormat(function(date) {
                            if (d3.timeYear(date) < date) {
                                return "";
                            } else {
                                return;
                            }
                        })
                    )
                    .call(g => g.select(".domain").remove());
                svg.append("g")
                    .call(xAxisSep);

                // Add Y axis
                const y = d3.scaleLinear()
                    .domain(adaptedYValueRange)
                    .range([ height, 0]);

                const line = d3.line()
                    .x(d => x(d.x))
                    .y(d => y(d.y))
                    .curve(d3.curveBasis);

                // Interactivity
                const mouseover = (d) => {
                    const target = d.target;

                    let element = d.target;
                    if (target.classList != "point__circle") {
                        element = target.parentNode.getElementsByClassName("point__circle")[0];
                    }
                    var all = document.getElementsByClassName("point-wrapper");
                    for (var i = 0; i < all.length; i++) {
                        all[i].style.opacity = 0.2;
                    }
                    // console.log(element.parentNode);
                    element.classList.add("hvr");
                    element.parentNode.style.opacity = 1;

                    let tooltip = target.parentNode.getElementsByClassName("point-active__tooltip")[0];
                    tooltip.style.display = "block";

                    // element.setAttribute("stroke-width", 22);
                    // if (target.parentNode.dataset.year === this.currentYear) {
                    // }
                };
                const click = (d) => {
                    // Do something on mouse click
                    const target = d.target;

                    let element = target;

                    if (target.classList !== "point__wrapper") {
                        element = target.parentNode;
                    }

                    this.$emit("year-clicked", parseInt(element.dataset.iday), element.dataset.year, element.dataset.domaine);
                };
                const mouseleave = function(d) {
                    const target = d.target;

                    let element = target.getElementsByClassName("point__circle")[0];

                    var all = document.getElementsByClassName("point-wrapper");
                    for (var i = 0; i < all.length; i++) {
                        all[i].style.opacity = 1;
                    }
                    element.classList.remove("hvr");

                    let tooltip = target.parentNode.getElementsByClassName("point-active__tooltip");
                    for (var j = 0; j < tooltip.length; j++) {
                        tooltip[j].style.display = "none";
                    }
                    // element.setAttribute("stroke-width", 0);
                };

                svg.append("g")
                    .call(xAxis);

                /* Define the data for the circles */
                var point = svg.append("g")
                    .selectAll("dot")
                    .data(this.cleanedData);

                /*Create and place the "blocks" containing the circle and the text */
                var pointGroup = point.enter()
                    .append("g")
                    .attr("class", (d) => {
                        let classValue = "point-wrapper point-wrapper--active";

                        return classValue;
                    })
                    .attr("data-year", (d) => {
                        return dayjs(d.x).year();
                    })
                    .attr("data-iday", (d) => {
                        return d.iday;
                    })
                    .attr("data-domaine", (d) => {
                        return d.domaine;
                    })
                    .on("mouseover", mouseover)
                    .on("click", click)
                    .on("mouseleave", mouseleave);

                // Create the line bewteen axis circle and circle
                pointGroup.append("path")
                    .attr("class", "link")
                    .attr("stroke", (d) => {
                        return d.color;
                    })
                    .attr("stroke-width", 2)
                    .attr("stroke-linecap", "round")
                    .attr("stroke-dasharray", "1, 8")
                    .attr("fill", "none")
                    .datum(d => {
                        const startPoint = {x: d.x, y: 0};
                        let middlePoint = {x: dayjs(d.x).add(4, "month"), y: d.y / 3};
                        let endPoint = {x: dayjs(d.x).add(6, "month"), y: d.y};

                        if (this.type === "month") {
                            middlePoint = {x: dayjs(d.x).add(8, "day"), y: d.y / 3};
                            endPoint = {x: dayjs(d.x).add(12, "day"), y: d.y};
                        }

                        const coordinates = [startPoint, middlePoint, endPoint];

                        return coordinates;
                    })
                    .attr("d", line);

                /*Create the circle for each block */
                pointGroup.append("circle")
                    .attr("class", "point__circle")
                    .attr("r", (d) => {
                        let pointRadius = 2 * d.value;
                        return pointRadius;
                    })
                    .attr("transform", (d) => {

                        let xOffset = x(dayjs(d.x).add(6, "month"));

                        if (this.type === "month") {
                            xOffset = x(dayjs(d.x).add(12, "day"));
                        }

                        return "translate("+ xOffset + ", " + y(d.y) + ")";
                    })
                    .attr("stroke", (d) => {
                        return hexToRgba(d.color, .2);
                    })
                    .attr("stroke-width", 0)
                    .attr("fill", (d) => {
                        return d.color;
                    });


                /* Create the text for each block */
                pointGroup.append("text")
                    .attr("class", "point__text")
                    .text((d, i) => {
                        let textContent = this.capitalize(d.domaine);
                        return textContent;
                    })
                    .attr("transform", (d) => {
                        let xOffset = x(dayjs(d.x).add(6, "month"));

                        if (this.type === "month") {
                            xOffset = x(dayjs(d.x).add(12, "day"));
                        }

                        return "translate("+ xOffset + ", " + y(d.y) + ")";
                    })
                    .attr("fill", "#230854")
                    .attr("text-anchor", "middle")
                    .attr("dominant-baseline", "central");

                // Create the axis circle of each group
                pointGroup.append("circle")
                    .attr("class", "circle-axis")
                    .attr("r", (d) => {
                        let pointRadius = 10;
                        return pointRadius;
                    })
                    .attr("transform", function(d) {
                        return "translate("+ x(d.x) + ", " + y(0) + ")";
                    })
                    .attr("stroke-width", 1)
                    .attr("fill", (d) => {
                        return d.color;
                    });

                // set desc on hover
                const tooltip = pointGroup.append("g")
                    .attr("class", "point-active__tooltip")
                    // .attr("style", "transform: translate(100px, 0);")
                    .attr("style", "display: none;")
                    .attr("transform", (d) => {
                        const xOffset = x(dayjs(d.x).add(250, "day"));
                        const yOffset  = y(d.y) - 90;
                        return "translate("+ xOffset + ", " + yOffset + ")";
                    });
                tooltip.append("rect")
                    .attr("class", "tooltip-rect")
                    .attr("y", -30)
                    .attr("rx", 10)
                    .attr("ry", 10)
                    .attr("width", 250)
                    .attr("height", 250);
                tooltip.append("text")
                    .each(function(d){
                        // split name by space and -
                        var year = d.x.toLocaleString("en-us", { month: "short" }) + " "+d.x.getFullYear();
                        var title = d.domaine;
                        var desc = d.desc;
                        var n = desc.match(/.{1,30}(\s|$)/g);
                        // get the current element
                        var text = d3.select(this);
                        text.append("tspan")
                            .attr("x", 5)
                            .attr("class", "point-active__tooltip-year")
                            .text(year);
                        text.append("tspan")
                            .attr("x", 5)
                            .attr("dy",1.6 + "em")
                            .attr("class", "point-active__tooltip-title")
                            .text(title);
                        // now loop
                        for (var i = 0; i < n.length; i++) {
                            text.append("tspan")
                                .attr("class", "point-active__tooltip-text")
                                .attr("x", 5)
                                .attr("dy", "1.6em")
                                .text(n[i]);
                        }
                    });
            });
        },
        handleMouseUp() {
            this.isMouseDown = false;
        },
        handleMouseDown() {
            this.isMouseDown = true;
            this.y = event.pageY;
            this.x = event.pageX;
            this.top = this.$el.scrollTop;
            this.left = this.$el.scrollLeft;
        },
        handleMouseMove(event) {
            this.isDragEnable = true;
            if (this.isDragEnable && this.isMouseDown) {
                this.$el.scrollLeft = this.left - event.pageX + this.x;
                this.$el.scrollTop = this.top - event.pageY + this.y;
            }
        },
        handleMouseLeave() {
            this.isDragEnable = false;
        }
    }
};
</script>
<style scoped>
		.scroll{
			position: absolute;
			left: 0; top: 0;
			width: 95%;
			z-index: 2;
		}
</style>

